import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";

import toastError from "../../errors/toastError";
import { dedupeTagsById } from "../../helpers/normalizeContactTags";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

function getTagSizeClass(name) {
	const len = String(name || "").length;
	if (len <= 8) return "tailwick-chat-item__tag--sm";
	if (len <= 14) return "tailwick-chat-item__tag--md";
	return "tailwick-chat-item__tag--lg";
}

function TagBadge({ tag, onRemove }) {
	return (
		<button
			type="button"
			className={clsx(
				"tailwick-chat-item__tag tailwick-contact-drawer__tag",
				getTagSizeClass(tag.name)
			)}
			style={{ backgroundColor: tag.color || "#64748b" }}
			title={tag.name}
			onClick={onRemove}
		>
			{tag.name}
		</button>
	);
}

export default function ContactDrawerTags({ contact }) {
	const containerRef = useRef(null);
	const [tags, setTags] = useState([]);
	const [selecteds, setSelecteds] = useState([]);
	const [adding, setAdding] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const contactId = contact?.id;

	useEffect(() => {
		let cancelled = false;

		const loadTags = async () => {
			try {
				const { data } = await api.get("/tags/list");
				if (cancelled) return;
				setTags(Array.isArray(data) ? data : []);
				setSelecteds(dedupeTagsById(contact?.tags || []));
			} catch (err) {
				if (!cancelled) toastError(err);
			}
		};

		setSelecteds(dedupeTagsById(contact?.tags || []));
		loadTags();

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contactId]);

	useEffect(() => {
		setSelecteds(dedupeTagsById(contact?.tags || []));
	}, [contactId, contact?.tags]);

	useEffect(() => {
		if (!adding) return undefined;

		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setAdding(false);
				setSearchTerm("");
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [adding]);

	const createTag = async (data) => {
		try {
			const { data: responseData } = await api.post("/tags", data);
			return responseData;
		} catch (err) {
			toastError(err);
			return null;
		}
	};

	const loadTags = async () => {
		try {
			const { data } = await api.get("/tags/list");
			setTags(Array.isArray(data) ? data : []);
			setSelecteds(dedupeTagsById(contact?.tags || []));
		} catch (err) {
			toastError(err);
		}
	};

	const syncTags = async (data) => {
		try {
			await api.post("/tags/sync", data);
		} catch (err) {
			toastError(err);
		}
	};

	const availableTags = useMemo(() => {
		const normalized = searchTerm.trim().toLowerCase();
		return tags.filter((tag) => {
			const alreadySelected = selecteds.some((selected) => selected.id === tag.id);
			if (alreadySelected) return false;
			if (!normalized) return true;
			return String(tag.name || "").toLowerCase().includes(normalized);
		});
	}, [tags, selecteds, searchTerm]);

	const applyChange = async (nextSelecteds) => {
		const unique = dedupeTagsById(nextSelecteds);
		setSelecteds(unique);
		await syncTags({ contactId: contact.id, tags: unique });
	};

	const handleSelect = async (tag) => {
		if (selecteds.some((s) => s.id === tag.id)) return;
		await applyChange([...selecteds, tag]);
		setSearchTerm("");
		setAdding(false);
	};

	const handleCreate = async () => {
		const name = searchTerm.trim();
		if (!name) return;

		const newTag = await createTag({ name, contactId: contact.id });
		if (!newTag) return;

		await loadTags();
		await applyChange([...selecteds, newTag]);
		setSearchTerm("");
		setAdding(false);
	};

	const handleRemove = async (index) => {
		const updated = selecteds.filter((_, i) => i !== index);
		await applyChange(updated);
	};

	const canCreate =
		searchTerm.trim().length > 0 &&
		!tags.some((tag) => tag.name.toLowerCase() === searchTerm.trim().toLowerCase());

	return (
		<div className="tailwick-contact-drawer__section" ref={containerRef}>
			<div className="tailwick-contact-drawer__section-head">
				<p className="tailwick-filter-panel__section-title mb-0">
					{i18n.t("tags.title")}
				</p>
				<button
					type="button"
					className="btn size-7 bg-default-150 text-default-600 hover:bg-default-200 hover:text-primary"
					onClick={() => setAdding(true)}
					aria-label={i18n.t("tags.title")}
				>
					<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
						<path d="M12 5v14" />
						<path d="M5 12h14" />
					</svg>
				</button>
			</div>

			{selecteds.length > 0 && (
				<div className="tailwick-chat-item__tags tailwick-contact-drawer__tags">
					{selecteds.map((tag, index) => (
						<TagBadge
							key={tag.id != null ? tag.id : `tag-${index}`}
							tag={tag}
							onRemove={() => handleRemove(index)}
						/>
					))}
				</div>
			)}
			{adding && (
				<div className="tailwick-contact-drawer__picker mt-2">
					<input
						type="text"
						className="form-input form-input-sm w-full"
						placeholder={i18n.t("chat.layout.filterTagsPlaceholder")}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						autoFocus
					/>
					<div className="tailwick-tags-multi__list mt-2">
						{availableTags.map((tag) => (
							<button
								key={tag.id}
								type="button"
								className="tailwick-tags-multi__option w-full"
								onClick={() => handleSelect(tag)}
							>
								<span
									className="tailwick-filter-panel__color-dot"
									style={{ backgroundColor: tag.color || "#64748b" }}
								/>
								<span className="tailwick-tags-multi__option-label">{tag.name}</span>
							</button>
						))}
						{canCreate && (
							<button
								type="button"
								className="tailwick-tags-multi__option w-full text-primary"
								onClick={handleCreate}
							>
								<span className="tailwick-tags-multi__option-label">
									{i18n.t("tags.buttons.add")}: &quot;{searchTerm.trim()}&quot;
								</span>
							</button>
						)}
						{availableTags.length === 0 && !canCreate && (
							<p className="tailwick-filter-panel__empty">—</p>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
